/**
 * Platform Context + hooks
 *
 * Kept in its own module (no component exports) so that React Fast Refresh
 * can hot-replace `PlatformProvider.tsx` in place without minting a new
 * context object on every edit.
 */

import { createContext, useContext } from 'react';
import type { PushPlatform } from './push';
import type { StoragePlatform } from './storage';
import type { NavigationPlatform } from './navigation';
import type { HapticsPlatform } from './haptics';
import type { PurchasePlatform } from './purchase';
import type { ReviewPlatform } from './review';

export interface PlatformContextValue {
  /** Whether running in native app */
  isNative: boolean;

  /** Native platform name (e.g., 'flutter') */
  platform: string | undefined;

  /** Push notifications service */
  push: PushPlatform;

  /** Storage service */
  storage: StoragePlatform;

  /** Navigation service */
  navigation: NavigationPlatform;

  /** Haptics service */
  haptics: HapticsPlatform;

  /** Purchase service (Stripe on web, RevenueCat on native) */
  purchase: PurchasePlatform;

  /** Review service (App Store / Play Store reviews) */
  review: ReviewPlatform;
}

export const PlatformContext = createContext<PlatformContextValue | null>(null);

export function usePlatform(): PlatformContextValue {
  const context = useContext(PlatformContext);
  if (!context) {
    throw new Error('usePlatform must be used within a PlatformProvider');
  }
  return context;
}

export function useIsNative(): boolean {
  return usePlatform().isNative;
}

export function useHaptics(): HapticsPlatform {
  return usePlatform().haptics;
}

export function usePlatformNavigation(): NavigationPlatform {
  return usePlatform().navigation;
}

export function usePurchase(): PurchasePlatform {
  return usePlatform().purchase;
}

export function useReview(): ReviewPlatform {
  return usePlatform().review;
}
